import type { i18n as I18n, TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { formatLocalized } from '../../utils/dateFormat';
import { AnalyticsGroupKey } from '@api/models/analytics/AnalyticsGroupKey';
import { VisitationAnalyticsGroupBy } from '@api/models/analytics/VisitationAnalyticsGroupBy';

export const computeDefaultDates = (): { firstOfMonth: string; todayStr: string } => {
  const now = DateTime.now();
  return {
    firstOfMonth: now.startOf('month').toISODate() ?? '',
    todayStr: now.toISODate() ?? '',
  };
};

export const dayToInstant = (day: string | null | undefined, bound: 'start' | 'end'): string | undefined => {
  if (!day) return undefined;
  const dt = DateTime.fromISO(day);
  if (!dt.isValid) return undefined;
  const bounded = bound === 'start' ? dt.startOf('day') : dt.endOf('day');
  return bounded.toISO() ?? undefined;
};

export const detectQuickPick = (from: string, to: string): string | null => {
  const now = DateTime.now();
  if (from === now.startOf('month').toISODate() && to === now.toISODate()) {
    return 'analytics.filter.quickPick.thisMonth';
  }
  const prev = now.minus({ months: 1 });
  if (from === prev.startOf('month').toISODate() && to === prev.endOf('month').toISODate()) {
    return 'analytics.filter.quickPick.lastMonth';
  }
  return null;
};

export const formatDateRange = (label: string, from: string | null, to: string | null, t: TFunction, i18n: I18n): string | null => {
  if (!from && !to) return null;
  if (from && to) {
    const quickPickKey = detectQuickPick(from, to);
    if (quickPickKey) return `${label}: ${t(quickPickKey)}`;
    return `${label}: ${formatLocalized(from, 'date', i18n)} – ${formatLocalized(to, 'date', i18n)}`;
  }
  if (from) {
    return `${label}: ${t('analytics.filter.from').toLowerCase()} ${formatLocalized(from, 'date', i18n)}`;
  }
  if (to) {
    return `${label}: ${t('analytics.filter.to').toLowerCase()} ${formatLocalized(to, 'date', i18n)}`;
  }
  return null;
};

// Time buckets arrive from the BE as a date_trunc instant in `groupKey.id` (`label` is always
// null there — see AbstractAnalyticsService); each time grouping renders that instant differently.
const TIME_BUCKET_FORMATTERS: Record<string, (dt: DateTime, i18n: I18n) => string> = {
  [VisitationAnalyticsGroupBy.Hour]: (dt) => dt.toLocaleString(DateTime.TIME_SIMPLE),
  [VisitationAnalyticsGroupBy.Day]: (dt) => dt.toLocaleString(DateTime.DATE_SHORT),
  [VisitationAnalyticsGroupBy.Week]: (dt, i18n) => i18n.t('analytics.chart.weekLabel', { week: dt.weekNumber }),
  [VisitationAnalyticsGroupBy.Month]: (dt) => dt.toLocaleString({ month: 'long', year: 'numeric' }),
};

const DRILLABLE_TIME_GROUP_BYS = new Set<string>([VisitationAnalyticsGroupBy.Month, VisitationAnalyticsGroupBy.Week, VisitationAnalyticsGroupBy.Day]);

export const isTimeGroupBy = (groupBy: string): boolean => groupBy in TIME_BUCKET_FORMATTERS;

export const isDrillableTimeGroupBy = (groupBy: string): boolean => DRILLABLE_TIME_GROUP_BYS.has(groupBy);

export const formatGroupLabel = (groupKey: AnalyticsGroupKey, groupBy: string, fallback: string, i18n: I18n): string => {
  const formatTimeBucket = TIME_BUCKET_FORMATTERS[groupBy];
  if (!formatTimeBucket) return groupKey.label || fallback;

  const instant = groupKey.id ?? groupKey.label;
  if (!instant) return fallback;

  const dt = DateTime.fromISO(instant.replace(' ', 'T'), { zone: 'utc' });
  return dt.isValid ? formatTimeBucket(dt.setLocale(i18n.language), i18n) : instant;
};

export const computeTimeBucketDayRange = (groupKey: AnalyticsGroupKey, groupBy: string): { start: string; end: string } | undefined => {
  const raw = groupKey.id ?? groupKey.label;
  if (!raw) return undefined;
  const dt = DateTime.fromISO(raw.replace(' ', 'T'), { zone: 'utc' });
  if (!dt.isValid) return undefined;
  switch (groupBy) {
    case VisitationAnalyticsGroupBy.Day: {
      const day = dt.toISODate();
      return day ? { start: day, end: day } : undefined;
    }
    case VisitationAnalyticsGroupBy.Week: {
      const start = dt.startOf('week').toISODate();
      const end = dt.endOf('week').toISODate();
      return start && end ? { start, end } : undefined;
    }
    case VisitationAnalyticsGroupBy.Month: {
      const start = dt.startOf('month').toISODate();
      const end = dt.endOf('month').toISODate();
      return start && end ? { start, end } : undefined;
    }
    default:
      return undefined;
  }
};
